import { instrumentRunQueryKeys, studyQueryKeys } from '@/config/queryKeys';
import { apiClient, type DownloadedFile } from '@/lib/api-client';
import type {
  InstrumentRunConsumablesPatch,
  InstrumentRunConsumablesUpdateResponse,
  InstrumentRunFilters,
  InstrumentRunPublic,
  InstrumentRunSearchRequest,
  InstrumentRunSortField,
  PaginatedInstrumentRunsResponse,
  SortOrder,
} from '@/types';

/**
 * Fetch a single instrument run by ID
 *
 * @param orgId - The organization ID
 * @param runId - The instrument run ID
 * @returns Promise resolving to the instrument run data
 *
 * @throws {ApiError} If the request fails or the run is not found
 *
 * @example
 * ```typescript
 * const run = await fetchInstrumentRun('org-123', 'run-456');
 * console.log(run.name, run.status);
 * ```
 */
export async function fetchInstrumentRun(orgId: string, runId: string): Promise<InstrumentRunPublic> {
  const result = await apiClient.get<InstrumentRunPublic>(instrumentRunQueryKeys.endpoints.detail(orgId, runId), {
    expectJson: true,
  });

  if (result === null) {
    throw new Error(`Instrument run ${runId} not found`);
  }

  return result;
}

/**
 * Study-scoped run search. Same request/response contract as the org-scoped
 * `POST /orgs/{org_id}/instrument_runs/search`, narrowed to one study.
 */
export async function searchStudyInstrumentRuns(
  orgId: string,
  studyId: string,
  request: InstrumentRunSearchRequest,
): Promise<PaginatedInstrumentRunsResponse> {
  const result = await apiClient.post<PaginatedInstrumentRunsResponse>(
    studyQueryKeys.endpoints.instrumentRunsSearch(orgId, studyId),
    request,
    { expectJson: true },
  );

  if (result === null) throw new Error('searchStudyInstrumentRuns: endpoint returned no content');
  return result;
}

export interface StudyInstrumentRunsListParams {
  skip?: number;
  limit?: number;
  sortBy?: InstrumentRunSortField;
  sortOrder?: SortOrder;
}

/**
 * Study-scoped run list. Pagination and sorting only — use `searchStudyInstrumentRuns` when
 * filters are needed.
 */
export async function fetchStudyInstrumentRuns(
  orgId: string,
  studyId: string,
  params: StudyInstrumentRunsListParams = {},
): Promise<PaginatedInstrumentRunsResponse> {
  const { limit, skip, sortBy, sortOrder } = params;

  const result = await apiClient.get<PaginatedInstrumentRunsResponse>(
    studyQueryKeys.endpoints.instrumentRuns(orgId, studyId),
    { query: { skip, limit, sort_by: sortBy, sort_order: sortOrder }, expectJson: true },
  );

  if (result === null) throw new Error('fetchStudyInstrumentRuns: endpoint returned no content');
  return result;
}

/** Status-independent (unlike the draft-only PUT). Omit a field to leave it unchanged, send
 *  `null` to clear it. */
export async function updateInstrumentRunConsumables(
  orgId: string,
  runId: string,
  payload: InstrumentRunConsumablesPatch,
): Promise<InstrumentRunConsumablesUpdateResponse> {
  const result = await apiClient.patch<InstrumentRunConsumablesUpdateResponse>(
    instrumentRunQueryKeys.endpoints.consumables(orgId, runId),
    payload,
    { expectJson: true },
  );

  if (result === null) throw new Error('updateInstrumentRunConsumables: endpoint returned no content');
  return result;
}

/** Streams a run's sample list as CSV. */
export async function downloadInstrumentRunSamplesCsv(orgId: string, runId: string): Promise<DownloadedFile> {
  return apiClient.download(instrumentRunQueryKeys.endpoints.samplesDownload(orgId, runId));
}

export interface InstrumentRunsDownloadOptions {
  sortBy?: InstrumentRunSortField;
  sortOrder?: SortOrder;
  filters?: InstrumentRunFilters | null;
}

// The backend defaults `limit` to 500 when omitted (unlike the paginated JSON endpoints, this
// route's `limit` has no upper bound — POR-374, precisely so a caller can pull everything in one
// file). Pass it explicitly so an org with 500+ runs doesn't get a silently truncated export.
const EXPORT_ALL_RUNS_LIMIT = 1_000_000;

/** Streams the org's full instrument-run list as CSV, honouring the grid's current sort and filters. */
export async function downloadInstrumentRunsCsv(
  orgId: string,
  options: InstrumentRunsDownloadOptions = {},
): Promise<DownloadedFile> {
  const { filters, sortBy, sortOrder } = options;

  return apiClient.download(instrumentRunQueryKeys.endpoints.download(orgId), {
    query: {
      limit: EXPORT_ALL_RUNS_LIMIT,
      sort_by: sortBy,
      sort_order: sortOrder,
      // The endpoint takes `filters` as a JSON-encoded query param, not a body.
      filters: filters ? JSON.stringify(filters) : undefined,
    },
  });
}
